import React, { useState } from 'react';
import { Project, Task, GanttItem } from '../types/Project';
import { format, differenceInDays, addDays, startOfWeek, endOfWeek } from 'date-fns';
import { ja } from 'date-fns/locale';

interface GanttChartProps {
  projects: Project[];
  tasks: Task[];
  selectedProject?: Project;
  onTaskClick?: (task: Task) => void;
}

export const GanttChart: React.FC<GanttChartProps> = ({ 
  projects, 
  tasks, 
  selectedProject,
  onTaskClick
}) => {
  // 折りたたみ状態を管理（プロジェクトIDをキーとしたオブジェクト）
  const [collapsedProjects, setCollapsedProjects] = useState<{[key: string]: boolean}>({});
  
  // プロジェクトの折りたたみ状態を切り替える
  const toggleProjectCollapse = (projectId: string) => {
    setCollapsedProjects(prev => ({
      ...prev,
      [projectId]: !prev[projectId]
    }));
  };

  // タスククリック時の処理
  const handleTaskClick = (item: GanttItem & { projectId?: string; assignee?: string }) => {
    if (item.type === 'task' && onTaskClick) {
      const task = tasks.find(t => t.id === item.id);
      if (task) {
        onTaskClick(task);
      }
    }
  };
  // データを統合してガントアイテムを作成（階層構造で）
  const ganttItems: (GanttItem & { projectId?: string; assignee?: string })[] = [];
  
  const filteredProjects = projects.filter(p => !selectedProject || p.id === selectedProject.id);
  
  filteredProjects.forEach(project => {
    // プロジェクトを追加
    ganttItems.push({
      id: project.id,
      name: project.name,
      startDate: project.startDate,
      endDate: project.endDate,
      progress: project.progress,
      type: 'project' as const
    });
    
    // 折りたたまれていない場合のみタスクを追加
    if (!collapsedProjects[project.id]) {
      const projectTasks = tasks.filter(t => t.projectId === project.id);
      projectTasks.forEach(task => {
        ganttItems.push({
          id: task.id,
          name: task.name,
          startDate: task.startDate,
          endDate: task.endDate,
          progress: task.progress,
          type: 'task' as const,
          dependencies: task.dependencies,
          projectId: task.projectId,
          assignee: task.assignee
        });
      });
    }
  });

  if (ganttItems.length === 0) {
    return (
      <div className="gantt-chart">
        <h2>ガントチャート</h2>
        <div className="empty-state">
          <p>表示する案件またはタスクがありません。</p>
        </div>
      </div>
    );
  }

  // 日付範囲を計算
  const allDates = ganttItems.flatMap(item => [item.startDate, item.endDate]);
  const minDate = startOfWeek(new Date(Math.min(...allDates.map(d => d.getTime()))));
  const maxDate = endOfWeek(new Date(Math.max(...allDates.map(d => d.getTime()))));
  
  const totalDays = differenceInDays(maxDate, minDate) + 1;
  const dayWidth = 12; // px per day (increased from 8 for better readability)
  
  // 週の配列を生成
  const weeks: Date[] = [];
  let currentWeek = minDate;
  while (currentWeek <= maxDate) {
    weeks.push(currentWeek);
    currentWeek = addDays(currentWeek, 7);
  }

  const calculatePosition = (date: Date) => {
    const daysDiff = differenceInDays(date, minDate);
    return daysDiff * dayWidth;
  };

  const calculateWidth = (startDate: Date, endDate: Date) => {
    const daysDiff = differenceInDays(endDate, startDate) + 1;
    return daysDiff * dayWidth;
  };

  return (
    <div className="gantt-chart">
      <h2>ガントチャート {selectedProject && `- ${selectedProject.name}`}</h2>
      
      <div className="gantt-container">
        <div className="gantt-header">
          <div className="gantt-labels">
            <div className="label-header">項目</div>
          </div>
          <div className="gantt-timeline" style={{ width: totalDays * dayWidth }}>
            <div className="timeline-header">
              {weeks.map((week, index) => (
                <div 
                  key={index} 
                  className="week-header"
                  style={{ 
                    left: calculatePosition(week),
                    width: Math.min(7 * dayWidth, totalDays * dayWidth - calculatePosition(week))
                  }}
                >
                  {format(week, 'MM/dd', { locale: ja })}
                </div>
              ))}
            </div>
            <div className="timeline-grid">
              {Array.from({ length: totalDays }, (_, i) => (
                <div 
                  key={i} 
                  className="day-line"
                  style={{ left: i * dayWidth }}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="gantt-body">
          {ganttItems.map((item, index) => (
            <div 
              key={item.id} 
              className={`gantt-row ${item.type === 'task' ? 'clickable' : ''}`}
              onClick={() => handleTaskClick(item)}
            >
              <div className="gantt-labels">
                <div className={`item-label ${item.type}`}>
                  {item.type === 'project' && (
                    <button 
                      className="collapse-button"
                      onClick={() => toggleProjectCollapse(item.id)}
                      title={collapsedProjects[item.id] ? 'タスクを表示' : 'タスクを非表示'}
                    >
                      {collapsedProjects[item.id] ? '▶' : '▼'}
                    </button>
                  )}
                  <div className="item-info">
                    <div className="item-main-info">
                      <span className="item-name">{item.name}</span>
                      {item.type === 'task' && (
                        <div className="item-details">
                          {item.assignee && (
                            <span className="item-assignee">👤 {item.assignee}</span>
                          )}
                          <span className="item-dates">
                            📅 {format(item.startDate, 'MM/dd')} - {format(item.endDate, 'MM/dd')}
                          </span>
                          {item.dependencies && item.dependencies.length > 0 && (
                            <span className="item-dependencies">
                              🔗 依存: {item.dependencies.length}件
                            </span>
                          )}
                        </div>
                      )}
                      {item.type === 'project' && (
                        <div className="project-summary">
                          📅 {format(item.startDate, 'yyyy/MM/dd')} - {format(item.endDate, 'yyyy/MM/dd')}
                          {(() => {
                            const projectTasks = tasks.filter(t => t.projectId === item.id);
                            return projectTasks.length > 0 && (
                              <span className="task-count">
                                📋 {projectTasks.length}件のタスク
                              </span>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="item-progress-section">
                    <span className="item-progress">{item.progress}%</span>
                    <div className="mini-progress-bar">
                      <div 
                        className="mini-progress-fill"
                        style={{ 
                          width: `${item.progress}%`,
                          backgroundColor: item.type === 'project' ? '#2563eb' : '#10b981'
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="gantt-timeline" style={{ width: totalDays * dayWidth }}>
                <div 
                  className={`gantt-bar ${item.type}`}
                  style={{
                    left: calculatePosition(item.startDate),
                    width: calculateWidth(item.startDate, item.endDate)
                  }}
                >
                  <div 
                    className="progress-bar"
                    style={{ width: `${item.progress}%` }}
                  />
                  <span className="bar-label">
                    {format(item.startDate, 'MM/dd')} - {format(item.endDate, 'MM/dd')}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
